import React, { useEffect, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarIcon, SunIcon, Half2Icon, MoonIcon } from '@radix-ui/react-icons';
import Stepper from './Stepper';
import {
  DIAS_OPCOES,
  MODO_DESC,
  MODO_LABEL,
  diasOpcaoDe,
  nomeCicloSugerido,
  parseIsoLocal,
  sugerirNovoCiclo,
  toIsoLocal,
} from './escalaUtils';

registerLocale('pt-BR', ptBR);

const ICONE_TURNO = { manha: <SunIcon />, tarde: <Half2Icon />, noite: <MoonIcon /> };

/**
 * Formulário de ciclo (criar e editar). Na criação sugere o ciclo de
 * pagamento corrente (16 → 15) — ou o seguinte, se aquele já existe — e
 * mantém o nome sincronizado com as datas enquanto o admin não o editar.
 */
const CicloFormModal = ({ aberto, ciclo, ciclosExistentes = [], busy = false, onClose, onSalvar }) => {
  const editando = !!ciclo;
  const [form, setForm] = useState(null);
  const [nomeManual, setNomeManual] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    if (ciclo) {
      setForm({
        nome: ciclo.nome,
        dataInicio: ciclo.dataInicio,
        dataFim: ciclo.dataFim,
        modo: ciclo.modo,
        vagasPadraoManha: ciclo.vagasPadraoManha,
        vagasPadraoTarde: ciclo.vagasPadraoTarde,
        vagasPadraoNoite: ciclo.vagasPadraoNoite,
        dias: diasOpcaoDe(ciclo),
        observacao: ciclo.observacao || '',
      });
      setNomeManual(true);
    } else {
      const sugestao = sugerirNovoCiclo(ciclosExistentes);
      setForm({
        nome: nomeCicloSugerido(sugestao.dataInicio, sugestao.dataFim),
        dataInicio: sugestao.dataInicio,
        dataFim: sugestao.dataFim,
        modo: 'escolha_admin',
        vagasPadraoManha: 4,
        vagasPadraoTarde: 4,
        vagasPadraoNoite: 0,
        dias: 'uteis',
        observacao: '',
      });
      setNomeManual(false);
    }
  }, [aberto, ciclo, ciclosExistentes]);

  if (!aberto || !form) return null;

  const setData = (campo, valor) => {
    const proximo = { ...form, [campo]: valor };
    if (!nomeManual) proximo.nome = nomeCicloSugerido(proximo.dataInicio, proximo.dataFim);
    setForm(proximo);
  };

  const submit = (e) => {
    e.preventDefault();
    const { dias, ...resto } = form;
    const opcao = DIAS_OPCOES.find((o) => o.key === dias) || DIAS_OPCOES[0];
    onSalvar({
      ...resto,
      incluirSabado: opcao.incluirSabado,
      incluirDomingo: opcao.incluirDomingo,
      nome: form.nome.trim(),
      observacao: form.observacao.trim() || null,
      vagasPadraoManha: Number(form.vagasPadraoManha),
      vagasPadraoTarde: Number(form.vagasPadraoTarde),
      vagasPadraoNoite: Number(form.vagasPadraoNoite),
    });
  };

  const periodoInvalido = !form.dataInicio || !form.dataFim || form.dataFim < form.dataInicio;

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal esc-modal-ciclo" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{editando ? 'Editar ciclo' : 'Novo ciclo da escala'}</h2>
            <p className="esc-modal-sub">
              {editando
                ? 'Mudar o período cria os dias que faltam e remove os dias vazios que sobrarem.'
                : 'Os dias do período são gerados com as vagas padrão; depois você ajusta dia a dia.'}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Fechar">
            &times;
          </button>
        </div>
        <form className="form esc-form" onSubmit={submit}>
          <div className="form-row">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="esc-nome">Nome do ciclo</label>
              <input
                id="esc-nome"
                value={form.nome}
                onChange={(e) => {
                  setNomeManual(true);
                  setForm({ ...form, nome: e.target.value });
                }}
                required
                maxLength={120}
              />
            </div>
            <div className="form-group">
              <label htmlFor="esc-inicio">Início</label>
              <div className="esc-datepicker">
                <DatePicker
                  id="esc-inicio"
                  selected={form.dataInicio ? parseIsoLocal(form.dataInicio) : null}
                  onChange={(date) => setData('dataInicio', date ? toIsoLocal(date) : '')}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  placeholderText="dd/mm/aaaa"
                  maxDate={form.dataFim ? parseIsoLocal(form.dataFim) : undefined}
                  required
                />
                <CalendarIcon className="esc-datepicker-icone" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="esc-fim">Fim</label>
              <div className="esc-datepicker">
                <DatePicker
                  id="esc-fim"
                  selected={form.dataFim ? parseIsoLocal(form.dataFim) : null}
                  onChange={(date) => setData('dataFim', date ? toIsoLocal(date) : '')}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  placeholderText="dd/mm/aaaa"
                  minDate={form.dataInicio ? parseIsoLocal(form.dataInicio) : undefined}
                  required
                />
                <CalendarIcon className="esc-datepicker-icone" />
              </div>
              {periodoInvalido && <small className="form-hint esc-warn">O fim deve ser depois do início.</small>}
            </div>
          </div>

          <div className="form-group">
            <label>Como as vagas são distribuídas</label>
            <div className="esc-modos">
              {['escolha_admin', 'ordem_chegada'].map((m) => (
                <label key={m} className={`esc-modo-card ${form.modo === m ? 'ativo' : ''}`}>
                  <input
                    type="radio"
                    name="esc-modo"
                    value={m}
                    checked={form.modo === m}
                    onChange={() => setForm({ ...form, modo: m })}
                  />
                  <span className="esc-modo-titulo">{MODO_LABEL[m]}</span>
                  <span className="esc-modo-desc">{MODO_DESC[m]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Vagas padrão por turno {editando && <span className="form-hint-inline">(usadas nos dias novos)</span>}</label>
            <div className="esc-vagas-padrao">
              {[
                ['manha', 'Manhã', 'vagasPadraoManha'],
                ['tarde', 'Tarde', 'vagasPadraoTarde'],
                ['noite', 'Noite', 'vagasPadraoNoite'],
              ].map(([key, label, campo]) => (
                <div key={key} className={`esc-vaga-padrao esc-turno-cor-${key}`}>
                  <span className="esc-vaga-padrao-rotulo">
                    {ICONE_TURNO[key]} {label}
                  </span>
                  <Stepper value={form[campo]} onChange={(v) => setForm({ ...form, [campo]: v })} ariaLabel={`vagas ${label}`} />
                </div>
              ))}
            </div>
            <small className="form-hint">Turno com 0 vagas não aparece no calendário. A noite fica desligada por padrão.</small>
          </div>

          <div className="form-group">
            <label>Dias da semana {editando && <span className="form-hint-inline">(vale para os dias novos)</span>}</label>
            <div className="esc-dias-opcoes">
              {DIAS_OPCOES.map((o) => (
                <label key={o.key} className={`esc-dia-opcao ${form.dias === o.key ? 'ativo' : ''}`}>
                  <input
                    type="radio"
                    name="esc-dias"
                    value={o.key}
                    checked={form.dias === o.key}
                    onChange={() => setForm({ ...form, dias: o.key })}
                  />
                  <span className="esc-dia-opcao-titulo">{o.label}</span>
                  <span className="esc-dia-opcao-desc">{o.desc}</span>
                </label>
              ))}
            </div>
            <small className="form-hint">Você sempre pode abrir ou fechar um sábado/domingo específico clicando no dia.</small>
          </div>

          <div className="form-group">
            <label htmlFor="esc-obs">Observações (opcional)</label>
            <textarea
              id="esc-obs"
              rows={2}
              value={form.observacao}
              onChange={(e) => setForm({ ...form, observacao: e.target.value })}
              placeholder="Aparece para os itinerantes no topo do calendário."
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={busy || periodoInvalido}>
              {busy ? 'Salvando…' : editando ? 'Salvar alterações' : 'Criar ciclo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CicloFormModal;
